import { ModalConfirmation, notify, useModal } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useDeleteProducts } from '@features/products/hooks/useDeleteProducts'
import { Product } from '@features/products/types/product'

import { ModalId } from '../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../../enums/queryKeys'

export interface DeleteProductsConfirmationModalProps {
  selectedProducts: Product[]
  onCancel: () => void
  onDeleteProductsSuccess?: () => void
  onDeleteProductsError?: () => void
}

export const DeleteProductsConfirmationModal = ({
  selectedProducts,
  onCancel,
  onDeleteProductsError,
  onDeleteProductsSuccess,
}: DeleteProductsConfirmationModalProps) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const { close } = useModal(ModalId.DeleteProductsConfirmationModal)

  const { delete: deleteProducts, isProcessing } = useDeleteProducts({
    onSuccess: () => {
      queryClient.invalidateQueries([QueryKeys.Products])
      if (selectedProducts?.length === 1) {
        notify({
          id: NotificationKeys.ProductDeleted,
          variant: 'success',
          message: t('products.table.actions.delete.notification.success', { name: selectedProducts[0].name }),
        })
      } else {
        notify({
          id: NotificationKeys.ProductBulkDeleted,
          variant: 'success',
          message: t('products.table.actions.delete.notification.success_other', { count: selectedProducts.length }),
        })
      }
      onDeleteProductsSuccess?.()
      close()
    },
    onError: () => {
      if (selectedProducts?.length === 1) {
        notify({
          id: NotificationKeys.ProductDeleted,
          variant: 'error',
          message: t('products.table.actions.delete.notification.error', { name: selectedProducts[0].name }),
        })
      } else {
        notify({
          id: NotificationKeys.ProductBulkDeleted,
          variant: 'error',
          message: t('products.table.actions.delete.notification.error_other', { count: selectedProducts.length }),
        })
      }
      close()
      onDeleteProductsError?.()
    },
  })

  const handleConfirm = useCallback(() => {
    const productIds = []
    for (const product of selectedProducts) {
      if (product.id) {
        productIds.push(product.id)
      }
    }
    deleteProducts(productIds)
  }, [deleteProducts, selectedProducts])

  const handleCancelClick = useCallback(() => {
    close()
    onCancel()
  }, [onCancel, close])

  return (
    <ModalConfirmation
      id={ModalId.DeleteProductsConfirmationModal}
      size="m"
      danger={true}
      title={t('products.table.actions.delete.confirmation_modal.title', { count: selectedProducts.length })}
      message={t('products.table.actions.delete.confirmation_modal.message', {
        name: selectedProducts[0]?.name,
        count: selectedProducts.length,
      })}
      cancelLabel={t('products.table.actions.delete.confirmation_modal.cancel')}
      okLabel={t('products.table.actions.delete.confirmation_modal.confirm_delete')}
      okLoading={isProcessing}
      onOk={handleConfirm}
      onCancel={handleCancelClick}
    />
  )
}
